import { useEffect, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Assessment } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ButtonTabs from '../components/ButtonTabs';
import ListHeader from '../components/ListHeader';
import CampaignReportsTab from '../components/reports/CampaignReportsTab';
import CallReportsTab from '../components/reports/CallReportsTab';
import UserActivityReportsTab from '../components/reports/UserActivityReportsTab';
import { usePermissions } from '../contexts/PermissionContext';
import usePageTitle from '../hooks/usePageTitle';


export default function ReportsPage() {
  const { t } = useTranslation(['pages']);
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const canAccessCampaignReports = hasPermission('campaigns_access');
  const canAccessCallReports = hasPermission('calls_reports_access');
  const canAccessUserActivityReports = hasPermission('users_read');

  usePageTitle(t('pages:reports.title'));

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canAccessCampaignReports) {
      tabs.push({
        id: 'campaigns',
        label: t('pages:reports.tabs.campaigns'),
        content: <CampaignReportsTab />,
      });
    }
    if (canAccessCallReports) {
      tabs.push({
        id: 'calls',
        label: t('pages:reports.tabs.calls'),
        content: <CallReportsTab />,
      });
    }
    if (canAccessUserActivityReports) {
      tabs.push({
        id: 'users',
        label: t('pages:reports.tabs.users'),
        content: <UserActivityReportsTab />,
      });
    }
    return tabs;
  }, [canAccessCallReports, canAccessCampaignReports, canAccessUserActivityReports, t]);

  const defaultTab = availableTabs[0]?.id ?? null;
  const selectedTab = searchParams.get('tab');
  const normalizedTab = availableTabs.some((tab) => tab.id === selectedTab) ? selectedTab : defaultTab;

  useEffect(() => {
    if (!normalizedTab) {
      return;
    }
    if (selectedTab === normalizedTab) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', normalizedTab);
    setSearchParams(next, { replace: true });
  }, [normalizedTab, searchParams, selectedTab, setSearchParams]);

  if (!canAccessCampaignReports && !canAccessCallReports && !canAccessUserActivityReports) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box>
      <ListHeader
        icon={<Assessment color="primary" fontSize="large" />}
        title={t('pages:reports.title')}
        subtitle={t('pages:reports.subtitle')}
      />
      {availableTabs.length > 0 ? (
        <ButtonTabs
          tabs={availableTabs}
          useUrl
          tabParam="tab"
          defaultValue={defaultTab ?? 'campaigns'}
          contentSx={{ mt: 3 }}
        />
      ) : null}
    </Box>
  );
}
